import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { getWidgetTier, isWidgetAvailable, type Tier } from "@emkit/builder-core";
import { useEffect, useState } from "react";
import { createDefaultWidget } from "../dnd/types.js";
import { useEditor } from "../store.js";

interface PaletteEntry {
  type: string;
  label: string;
  icon: string;
}

interface PaletteGroup {
  title: string;
  items: PaletteEntry[];
}

const groups: PaletteGroup[] = [
  {
    title: "Basics",
    items: [
      { type: "heading", label: "Heading", icon: "H" },
      { type: "text", label: "Text", icon: "T" },
      { type: "button", label: "Button", icon: "◉" },
      { type: "badge", label: "Badge", icon: "●" },
      { type: "spacer", label: "Spacer", icon: "│" },
      { type: "divider", label: "Divider", icon: "—" },
    ],
  },
  {
    title: "Sections",
    items: [
      { type: "hero", label: "Hero", icon: "★" },
      { type: "features", label: "Features", icon: "❖" },
      { type: "pricing", label: "Pricing", icon: "$" },
      { type: "testimonials", label: "Testimonials", icon: "❝" },
      { type: "stats", label: "Stats", icon: "#" },
      { type: "logo-bar", label: "Logo Bar", icon: "▭" },
      { type: "cta-banner", label: "CTA Banner", icon: "➤" },
      { type: "faq", label: "FAQ", icon: "?" },
    ],
  },
  {
    title: "Media",
    items: [
      { type: "image", label: "Image", icon: "▢" },
      { type: "video", label: "Video", icon: "▶" },
      { type: "audio", label: "Audio", icon: "♪" },
      { type: "gallery", label: "Gallery", icon: "▦" },
      { type: "carousel", label: "Carousel", icon: "⇋" },
    ],
  },
  {
    title: "Content",
    items: [
      { type: "accordion", label: "Accordion", icon: "≡" },
      { type: "tabs", label: "Tabs", icon: "⊟" },
      { type: "icon-list", label: "Icon List", icon: "✓" },
      { type: "quote", label: "Quote", icon: "❞" },
      { type: "alert", label: "Alert", icon: "⚠" },
      { type: "code", label: "Code", icon: "{}" },
    ],
  },
  {
    title: "Interactive",
    items: [
      { type: "form", label: "Form", icon: "✉" },
      { type: "newsletter", label: "Newsletter", icon: "@" },
      { type: "share", label: "Share", icon: "↗" },
      { type: "social-links", label: "Social Links", icon: "⊗" },
      { type: "countdown", label: "Countdown", icon: "⏱" },
    ],
  },
  {
    title: "Data",
    items: [
      { type: "chart", label: "Chart", icon: "▣" },
      { type: "progress", label: "Progress", icon: "▬" },
      { type: "map", label: "Map", icon: "◉" },
    ],
  },
  {
    title: "People & Process",
    items: [
      { type: "team", label: "Team", icon: "👥" },
      { type: "steps", label: "Steps", icon: "①" },
      { type: "timeline", label: "Timeline", icon: "┃" },
    ],
  },
  {
    title: "Navigation",
    items: [
      { type: "nav-menu", label: "Nav Menu", icon: "☰" },
      { type: "breadcrumbs", label: "Breadcrumbs", icon: "›" },
    ],
  },
  {
    title: "Commerce",
    items: [{ type: "product-card", label: "Product", icon: "🛒" }],
  },
  {
    title: "Advanced",
    items: [{ type: "html", label: "HTML", icon: "</>" }],
  },
];

const STORAGE_KEY = "emk-palette-collapsed";
const DEFAULT_OPEN = new Set(["Basics", "Sections"]);

function loadCollapsed(): Record<string, boolean> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveCollapsed(state: Record<string, boolean>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / disabled storage */
  }
}

export function Palette() {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    const stored = loadCollapsed();
    // If nothing stored yet, collapse everything except the defaults.
    if (Object.keys(stored).length === 0) {
      const init: Record<string, boolean> = {};
      for (const g of groups) init[g.title] = !DEFAULT_OPEN.has(g.title);
      return init;
    }
    return stored;
  });

  useEffect(() => {
    saveCollapsed(collapsed);
  }, [collapsed]);

  const toggle = (title: string) =>
    setCollapsed((prev) => ({ ...prev, [title]: !prev[title] }));

  const expandAll = () => {
    const next: Record<string, boolean> = {};
    for (const g of groups) next[g.title] = false;
    setCollapsed(next);
  };
  const collapseAll = () => {
    const next: Record<string, boolean> = {};
    for (const g of groups) next[g.title] = true;
    setCollapsed(next);
  };

  return (
    <aside className="emke-palette">
      <div
        style={{
          display: "flex",
          gap: 6,
          padding: "10px 12px 8px",
          borderBottom: "1px solid var(--border)",
          marginBottom: 4,
        }}
      >
        <button
          type="button"
          className="emke-btn"
          onClick={expandAll}
          style={{ flex: 1, fontSize: 11, padding: "4px 8px" }}
        >
          Expand all
        </button>
        <button
          type="button"
          className="emke-btn"
          onClick={collapseAll}
          style={{ flex: 1, fontSize: 11, padding: "4px 8px" }}
        >
          Collapse all
        </button>
      </div>

      {groups.map((g) => {
        const isCollapsed = collapsed[g.title] ?? false;
        return (
          <div key={g.title}>
            <button
              type="button"
              onClick={() => toggle(g.title)}
              className="emke-palette-group-header"
              aria-expanded={!isCollapsed}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 16px 6px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                font: "inherit",
                color: "var(--fg-muted)",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              <span>
                {g.title} ({g.items.length})
              </span>
              <span
                aria-hidden="true"
                style={{
                  fontSize: 10,
                  opacity: 0.7,
                  transition: "transform 0.15s",
                  transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                }}
              >
                ▾
              </span>
            </button>
            {!isCollapsed && g.items.map((e) => <PaletteItem key={e.type} entry={e} />)}
          </div>
        );
      })}
      <div
        style={{
          padding: "16px 16px 20px",
          color: "var(--fg-dim)",
          fontSize: 11,
          lineHeight: 1.5,
        }}
      >
        Tip: click to append to the first column, or drag into the canvas for precise
        placement.
      </div>
    </aside>
  );
}

function PaletteItem({ entry }: { entry: PaletteEntry }) {
  const appendWidget = useEditor((s) => s.appendWidget);
  const plan = useEditor((s) => s.plan);
  const tier = getWidgetTier(entry.type);
  const locked = !isWidgetAvailable(entry.type, plan);

  // Disable drag for locked widgets — pulling the trick to omit the
  // dnd hook entirely (rather than passing disabled) keeps the dom
  // attributes/listeners clean.
  const draggable = useDraggable({
    id: `palette:${entry.type}`,
    data: { kind: "palette", widgetType: entry.type },
    disabled: locked,
  });

  const onClick = () => {
    if (locked) return;
    const w = createDefaultWidget(entry.type);
    if (w) appendWidget(w);
  };

  const title = locked
    ? `${entry.label} — requires ${tier === "agency" ? "Agency" : "Pro"} plan`
    : `Add ${entry.label}`;

  return (
    <button
      ref={draggable.setNodeRef}
      type="button"
      className="emke-palette-item"
      title={title}
      onClick={onClick}
      style={{
        transform: CSS.Translate.toString(draggable.transform),
        opacity: draggable.isDragging ? 0.5 : locked ? 0.55 : 1,
        cursor: locked ? "not-allowed" : draggable.isDragging ? "grabbing" : "grab",
      }}
      {...(locked
        ? { "aria-disabled": true as const }
        : { ...draggable.attributes, ...draggable.listeners })}
    >
      <span
        style={{
          width: 24,
          textAlign: "center",
          color: "var(--fg-muted)",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
        }}
      >
        {entry.icon}
      </span>
      <span style={{ flex: 1 }}>{entry.label}</span>
      {locked && <TierBadge tier={tier} />}
    </button>
  );
}

function TierBadge({ tier }: { tier: Tier }) {
  if (tier === "free") return null;
  const label = tier === "pro" ? "Pro" : "Agency";
  return (
    <span
      className="emke-tier-badge"
      style={{
        marginLeft: "auto",
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.06em",
        padding: "2px 6px",
        borderRadius: 4,
        background:
          tier === "agency"
            ? "linear-gradient(135deg, #d97706, #f59e0b)"
            : "linear-gradient(135deg, #7c3aed, #a78bfa)",
        color: "#fff",
      }}
    >
      🔒 {label.toUpperCase()}
    </span>
  );
}
